import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { apiErrorSchema } from '@homecare/contracts';

import { ApiRequestError, request, requestMultipart, type RequestOptions } from './api';
import { refreshSession, useSession } from './session';

/**
 * The API, bound to whoever is signed in.
 *
 * Every screen calls through this rather than importing `request` directly, so
 * the access token is attached in one place. A component that reached for the
 * token itself would be a component that could forget to.
 */

/**
 * Run a request, and if the access token had expired, refresh once and retry.
 *
 * ## Why this exists at all
 *
 * The console stored a refresh token from the day sign-in was built and never
 * sent it anywhere. Access tokens last 15 minutes (slice-00 §3), so a
 * coordinator filling in an intake form was signed out mid-form and lost it.
 * The API's rotation was correct and tested the whole time; nothing called it.
 * `endpoint-reachability.spec.ts` is what surfaced that.
 *
 * ## Once, and only on an expired token
 *
 * A second 401 after a fresh token is not an expiry — it is a permission the
 * caller does not hold, or a session revoked underneath them. Retrying again
 * would loop, so the retry is one-shot and the second answer is the one the
 * screen renders.
 *
 * `AUTH_FORBIDDEN` is deliberately not retried: it answers 403 and is about the
 * caller's permissions, which no amount of refreshing changes.
 */
async function withRefresh<T>(run: (token: string | null) => Promise<T>, token: string | null) {
  try {
    return await run(token);
  } catch (error) {
    if (!(error instanceof ApiRequestError) || error.status !== 401) throw error;

    const rotated = await refreshSession();
    if (rotated === null) throw error;

    return run(rotated);
  }
}

export function useApi(): {
  get: <T>(path: string, options?: RequestOptions) => Promise<T>;
  send: <T>(
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
  ) => Promise<T>;
  upload: <T>(path: string, file: File, fields?: Record<string, string>) => Promise<T>;
  /**
   * A multipart body the caller has assembled.
   *
   * `upload` takes one file and a bag of strings, which fits the CSV import. A
   * credential is fields *and* an optional document in one request — §17.2's
   * direct upload path — and a two-call flow that can half-succeed leaves a
   * credential row with no document, recorded by somebody who believed they
   * had attached one.
   */
  postForm: <T>(path: string, form: FormData) => Promise<T>;
  /** The raw response, for a download rather than an envelope. */
  download: (path: string) => Promise<{ blob: Blob; filename: string }>;
} {
  const accessToken = useSession((state) => state.accessToken);

  const get = useCallback(
    <T>(path: string, options: RequestOptions = {}): Promise<T> =>
      withRefresh((token) => request<T>(path, { ...options, accessToken: token }), accessToken),
    [accessToken],
  );

  const send = useCallback(
    <T>(method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> =>
      withRefresh(
        (token) =>
          request<T>(path, {
            method,
            accessToken: token,
            ...(body === undefined ? {} : { body }),
          }),
        accessToken,
      ),
    [accessToken],
  );

  const upload = useCallback(
    <T>(path: string, file: File, fields: Record<string, string> = {}): Promise<T> =>
      withRefresh((token) => requestMultipart<T>(path, file, fields, token), accessToken),
    [accessToken],
  );

  const postForm = useCallback(
    async <T>(path: string, form: FormData): Promise<T> => {
      const response = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        // No content-type header: the browser sets it, with the boundary.
        // Setting it by hand omits the boundary and the server sees a body it
        // cannot parse.
        headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
        body: form,
      });

      const body: unknown = await response.json().catch(() => null);

      if (!response.ok) {
        throw new ApiRequestError(response.status, apiErrorSchema.parse(body));
      }

      return body as T;
    },
    [accessToken],
  );

  const download = useCallback(
    async (path: string): Promise<{ blob: Blob; filename: string }> => {
      const response = await fetch(`${API_BASE}${path}`, {
        headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
      });

      if (!response.ok) {
        // A failed download still owes the person a sentence. The envelope is
        // JSON even when the success body would not have been.
        const text = await response.text();
        throw new Error(text || 'That file could not be downloaded.');
      }

      const disposition = response.headers.get('content-disposition') ?? '';
      const match = /filename="([^"]+)"/.exec(disposition);

      return { blob: await response.blob(), filename: match?.[1] ?? 'download.csv' };
    },
    [accessToken],
  );

  return { get, send, upload, postForm, download };
}

export const API_BASE = (import.meta.env['VITE_API_URL'] as string) ?? 'http://localhost:3000';

/** Invalidate every client query. Used after any write that could change a list. */
export function useRefreshClients(): () => Promise<void> {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['clients'] });
  }, [queryClient]);
}
